import { useState, useEffect } from "react";
import { Check, X, Eye, Clock, Mail, User, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import axios from "axios";
import { API } from "@/App";
import AdminPagination from "@/components/admin/AdminPagination";

const statusColors = {
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
};

const statusLabels = {
  pending: "Pendiente",
  approved: "Aprobado",
  rejected: "Rechazado",
};

const categoryLabels = {
  general: "Historia",
  "antes-despues": "Antes/Después",
  tradicion: "Tradición",
  personaje: "Personaje",
  oficio: "Oficio",
};

export default function AdminSubmissions() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  useEffect(() => {
    fetchSubmissions();
  }, [statusFilter, page, limit]);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.append("status", statusFilter);
      params.append("skip", String((page - 1) * limit));
      params.append("limit", String(limit));

      const response = await axios.get(`${API}/admin/submissions?${params}`, {
      });
      setSubmissions(response.data.items);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching submissions:", error);
      toast.error("Error al cargar las solicitudes");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (submissionId, newStatus) => {
    try {
      const formData = new FormData();
      formData.append("status", newStatus);
      
      await axios.put(`${API}/admin/submissions/${submissionId}`, formData, {
      });
      toast.success(`Solicitud ${statusLabels[newStatus].toLowerCase()}`);
      fetchSubmissions();
      setSelectedSubmission(null);
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const pendingCount = submissions.filter((s) => s.status === "pending").length;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-submissions">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-brand-text">
              Solicitudes de la Comunidad
            </h1>
            <p className="text-brand-textSecondary mt-1">
              {pendingCount > 0 ? (
                <span className="text-yellow-600 font-medium">
                  {pendingCount} solicitudes pendientes de revisión
                </span>
              ) : (
                `${total} solicitudes en total`
              )}
            </p>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filtrar por estado" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              <SelectItem value="pending">Pendientes</SelectItem>
              <SelectItem value="approved">Aprobadas</SelectItem>
              <SelectItem value="rejected">Rechazadas</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-yellow-50 rounded-2xl p-4 text-center">
            <p className="text-3xl font-bold text-yellow-600">
              {submissions.filter((s) => s.status === "pending").length}
            </p>
            <p className="text-sm text-yellow-700">Pendientes</p>
          </div>
          <div className="bg-green-50 rounded-2xl p-4 text-center">
            <p className="text-3xl font-bold text-green-600">
              {submissions.filter((s) => s.status === "approved").length}
            </p>
            <p className="text-sm text-green-700">Aprobadas</p>
          </div>
          <div className="bg-red-50 rounded-2xl p-4 text-center">
            <p className="text-3xl font-bold text-red-600">
              {submissions.filter((s) => s.status === "rejected").length}
            </p>
            <p className="text-sm text-red-700">Rechazadas</p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Título</TableHead>
                <TableHead>Autor</TableHead>
                <TableHead>Categoría</TableHead>
                <TableHead>Fotos</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={7}>
                      <div className="h-12 bg-gray-100 animate-pulse rounded" />
                    </TableCell>
                  </TableRow>
                ))
              ) : submissions.length > 0 ? (
                submissions.map((sub) => (
                  <TableRow key={sub.submission_id} className={sub.status === "pending" ? "bg-yellow-50/50" : ""}>
                    <TableCell>
                      <div className="font-medium text-brand-text line-clamp-1">
                        {sub.title}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <div>
                          <p className="text-sm">{sub.author_name}</p>
                          <p className="text-xs text-gray-400">{sub.author_email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-brand-textSecondary">
                        {categoryLabels[sub.category] || sub.category}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 text-sm text-brand-textSecondary">
                        <ImageIcon className="h-4 w-4" />
                        {sub.image_ids?.length || 0}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusColors[sub.status]}>
                        {statusLabels[sub.status]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-brand-textSecondary text-sm">
                      {sub.created_at
                        ? new Date(sub.created_at).toLocaleDateString("es-HN")
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setSelectedSubmission(sub)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {sub.status === "pending" && (
                          <>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-green-600 hover:text-green-700"
                              onClick={() => updateStatus(sub.submission_id, "approved")}
                            >
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-red-600 hover:text-red-700"
                              onClick={() => updateStatus(sub.submission_id, "rejected")}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <p className="text-brand-textSecondary">No hay solicitudes</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Detail Dialog */}
        <Dialog open={!!selectedSubmission} onOpenChange={() => setSelectedSubmission(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Detalle de Solicitud</DialogTitle>
            </DialogHeader>
            {selectedSubmission && (
              <div className="space-y-4 mt-4">
                <div>
                  <h3 className="font-heading text-xl font-bold text-brand-text">
                    {selectedSubmission.title}
                  </h3>
                  <Badge className={`mt-2 ${statusColors[selectedSubmission.status]}`}>
                    {statusLabels[selectedSubmission.status]}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Autor</p>
                    <p className="font-medium">{selectedSubmission.author_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Email</p>
                    <p className="font-medium">{selectedSubmission.author_email}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Categoría</p>
                    <p className="font-medium">{categoryLabels[selectedSubmission.category]}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Año aproximado</p>
                    <p className="font-medium">{selectedSubmission.year_approx || "-"}</p>
                  </div>
                </div>

                <div>
                  <p className="text-gray-500 text-sm mb-2">Descripción</p>
                  <p className="text-brand-text bg-gray-50 p-4 rounded-xl">
                    {selectedSubmission.description}
                  </p>
                </div>

                {selectedSubmission.images && selectedSubmission.images.length > 0 && (
                  <div>
                    <p className="text-gray-500 text-sm mb-2">Fotos enviadas</p>
                    <div className="grid grid-cols-3 gap-2">
                      {selectedSubmission.images.map((img, idx) => (
                        <img
                          key={idx}
                          src={img.url}
                          alt={`Foto ${idx + 1}`}
                          className="w-full aspect-square object-cover rounded-lg"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {selectedSubmission.status === "pending" && (
                  <div className="flex gap-3 pt-4 border-t">
                    <Button
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => updateStatus(selectedSubmission.submission_id, "approved")}
                    >
                      <Check className="h-4 w-4 mr-2" />
                      Aprobar
                    </Button>
                    <Button
                      variant="destructive"
                      className="flex-1"
                      onClick={() => updateStatus(selectedSubmission.submission_id, "rejected")}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Rechazar
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
        <AdminPagination
          page={page}
          total={total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={(l) => { setLimit(l); setPage(1); }}
          testId="submissions-pagination"
          label="solicitudes"
        />
    </AdminLayout>
  );
}
